import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import conversationService from './conversationService'
import { STATUS } from 'features/enums'
import axios from 'axios'
import { baseUrl, config } from 'features/constants'
import { notificationSlice } from 'features/notifs/notifSlice'
import { encryptMessage, decryptMessage } from 'services/e2ee/cryptoService'
import { getPrivateKey } from 'services/e2ee/keyStore'
 
const URL = baseUrl

const errorMessage = (error) => {
  return (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
}

const initialState = {
  conversations: [],
  conversation: null,
  getConversationsStatus: STATUS.NOT_STARTED,
  createConversationStatus: STATUS.NOT_STARTED,
  messages: [], 
  hasMore: false,
  getMessagesStatus:  STATUS.NOT_STARTED,
  sendMessageStatus: STATUS.NOT_STARTED,

  currentConversation: '',
  currentMessagePanel: {
    id: null,
    image: null,
    name: null,
  },

  viewStartConversationModal: false,

  conversationType: '',

  isSearchUsersError: false,
  isSearchUsersSuccess: false,
  isSearchUsersLoading: false,
  searchUsersErrorMessage: '',

  users: [],

  church: null, // the church of the user should always be included in when getting the church members
  churches: [] , 

  isGetChurchMembersLoading: false,
  isGetChurchMembersError: false,
  isGetChurchMembersSuccess: false,
  getChurchMembersErrorMessage: '',

  markAsReadStatus: STATUS.NOT_STARTED,

  searchUserResults: [],
  isSearchLoading: false,
  isSearchSuccess: false,
  isSearchError: false,

  totalMembers: 0,
  totalPages: 0,
 

  conversationsMessage: '',

  sendPrayselyMessageStatus: STATUS.NOT_STARTED,
}

export const createConversation = createAsyncThunk(
  'conversations/createConversation',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.createConversation(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getPrayselyConversations = createAsyncThunk(
  'conversations/getPrayselyConversations',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.getPrayselyConversations(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)


export const searchUsers = createAsyncThunk(
  'conversations/searchUsers',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.searchUsers(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getConversationsWithOtherMembers = createAsyncThunk(
  'conversations/getConversationsWithOtherMembers',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.getConversationsWithOtherMembers(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getChurchMembers = createAsyncThunk(
  'conversations/getChurchMembers',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.getChurchMembers(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const searchMembers = createAsyncThunk(
  'conversations/searchMembers',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.searchMembers(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const deleteConversation = createAsyncThunk(
  'conversations/deleteConversation',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.deleteConversation(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const cleanupConversations = createAsyncThunk(
  'conversations/cleanupConversations',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.cleanupConversations(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const markConversationAsRead = createAsyncThunk(
  'conversations/markConversationAsRead',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.markConversationAsRead(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const sendPrayselyMessage = createAsyncThunk(
  'conversations/sendPrayselyMessage',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.sendPrayselyMessage(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const sendMessageWithConvId = createAsyncThunk(
  'messages/sendMessageWithConvId',
  async (data, thunkAPI) => {
    try {
      const token  = thunkAPI.getState().auth.user.token
      const userId = thunkAPI.getState().auth.user._id

      // data may be FormData (with optional file) or a plain object.
      // Extract text + conversationId so we can encrypt before sending.
      const isFormData       = data instanceof FormData
      const plainText        = isFormData ? (data.get('text') ?? '') : (data.text ?? '')
      const conversationId   = isFormData ? data.get('conversationId') : data.conversationId
      const recipientPublicKey = isFormData ? data.get('recipientPublicKey') : data.recipientPublicKey

      let payload = data  // default: send as-is

      if (recipientPublicKey && plainText) {
        const myPrivateKey = getPrivateKey(userId)
        if (myPrivateKey) {
          const encrypted = encryptMessage(String(plainText), String(recipientPublicKey), myPrivateKey)
          // Build a plain-object payload (not FormData) for encrypted messages
          payload = {
            conversationId,
            text:       '',
            ciphertext: encrypted.ciphertext,
            nonce:      encrypted.nonce,
          }
          // If there was a file, keep it in a new FormData (files can't be E2EE encrypted yet)
          if (isFormData && data.get('file')) {
            const fd = new FormData()
            fd.append('file', data.get('file'))
            fd.append('conversationId', conversationId)
            fd.append('text', '')
            fd.append('ciphertext', encrypted.ciphertext)
            fd.append('nonce', encrypted.nonce)
            payload = fd
          }
        }
      }

      const response = await conversationService.sendMessageWithConvId(payload, token)

      // Patch plaintext back into the returned message so it renders immediately
      const savedMsg = response?.message
      if (savedMsg && recipientPublicKey && plainText) {
        return { ...response, message: { ...savedMsg, text: String(plainText) } }
      }

      return response
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getMessages = createAsyncThunk(
  'messages/getMessages',
  async ({ conversationId, otherPartyPublicKey }, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token

      const notificationsState = thunkAPI.getState().notifications
      const response = await axios.post(
        URL + '/api/messages/getPrayselyConversationMessages',
        { conversationId },
        config(token)
      )

      // Decrypt E2EE messages if we have the other party's public key
      let messages = response.data?.messages ?? response.data ?? []
      if (otherPartyPublicKey && Array.isArray(messages)) {
        const myPrivateKey = getPrivateKey(thunkAPI.getState().auth.user._id)
        if (myPrivateKey) {
          messages = messages.map((msg) => {
            if (msg.ciphertext && msg.nonce) {
              const plaintext = decryptMessage(msg.ciphertext, msg.nonce, otherPartyPublicKey, myPrivateKey)
              return { ...msg, text: plaintext ?? msg.text }
            }
            return msg
          })
        }
      }

      // Remove notification for this conversation
      const newMessageNotification = notificationsState.newMessageNotifications.find(
        (n) => n.conversationId === conversationId
      )
      if (newMessageNotification) {
        thunkAPI.dispatch(notificationSlice.actions.removeNewMessageNotification(newMessageNotification._id))
      }

      if (Array.isArray(response.data)) return messages
      return { ...response.data, messages }

    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getMoreMessages = createAsyncThunk(
  'messages/getMoreMessages',
  async (conversationId, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.getMessages(conversationId, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)


export const deleteMessage = createAsyncThunk(
  'messages/deleteMessage',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.deleteMessage(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const addNewMessageNotification = createAsyncThunk(
  'messages/addNewMessageNotification',
  async (data, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token
      return await conversationService.addNewMessageNotification(data, token)
    } catch (error) {
      const message = errorMessage(error)
      return thunkAPI.rejectWithValue(message)
    }
  }
)



export const conversationSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    conversationReset: (state) => initialState,

    chooseConversation: (state, action) => {
      state.viewConversation = action.payload
    },

    setViewAllUsers: (state, action) => {
      state.viewAllUsers = action.payload
    },

    resetAlreadyExists: (state) => {
      state.alreadyExists = false
      state.alreadyExistsId = ''
    },

    resetDeleteData: (state) => { 
      state.deleteConversationSuccess = false
      state.deleteConversationLoading = false
    },

    viewNoConversation: (state) => {
      state.viewConversation = { id: null, fullName: null }
      // state.viewAllUsers = false
    },
    setPanelInfo: (state, action) => {
      state.currentMessagePanel.id = action.payload.id
      state.currentMessagePanel.image = action.payload.image
      state.currentMessagePanel.name = action.payload.name
    },
    setViewStartConversationModal: (state, action) => {
      state.viewStartConversationModal = action.payload
    },
    resetCreateConversation: (state) => {
      state.isCreateConversationError = false
      state.isCreateConversationSuccess = false
      state.isCreateConversationLoading = false
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPrayselyConversations.pending, (state) => {
        state.getConversationsStatus = STATUS.LOADING
      })
      .addCase(getPrayselyConversations.fulfilled, (state, action) => {
        state.getConversationsStatus = STATUS.SUCCESS
        state.conversations = action.payload.conversations
      })
      .addCase(getPrayselyConversations.rejected, (state, action) => {
        state.getConversationsStatus = STATUS.ERROR 
      })
      // ----------------------------------------------------------------------- 
      .addCase(searchUsers.pending, (state) => {
        state.isSearchUsersLoading = true
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.isSearchUsersError = false
        state.isSearchUsersSuccess = true
        state.isSearchUsersLoading = false
        state.searchUserResults = action.payload
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.isSearchUsersError = true
        state.isSearchUsersSuccess = false
        state.isSearchUsersLoading = false
        state.searchUsersErrorMessage = action.payload
      })

    // -----------------------------------------------------------------------
    .addCase(getChurchMembers.pending, (state) => {
      state.isGetChurchMembersLoading = true
    })
    .addCase(getChurchMembers.fulfilled, (state, action) => {
      state.isGetChurchMembersError = false
      
      state.isGetChurchMembersLoading = false
      state.churches = action.payload.churches

      if(action.payload.pageNumber === 1) {
        state.users = action.payload.users
      }else{
        state.users.push(...action.payload.users)
      }

      state.totalMembers = action.payload.totalMembers
      state.totalPages = action.payload.totalPages
      state.pageNumber = action.payload.pageNumber
      state.hasPrevPage = action.payload.hasPrevPage
      state.hasNextPage = action.payload.hasNextPage
      state.prevPage = action.payload.prevPage
      state.nextPage = action.payload.nextPage
      state.isGetChurchMembersSuccess = true

    })
    .addCase(getChurchMembers.rejected, (state, action) => {
      state.isGetChurchMembersError = true
      state.isGetChurchMembersSuccess = false
      state.isGetChurchMembersLoading = false
      state.getChurchMembersErrorMessage = action.payload
    })
    // -----------------------------------------------------------------------
    .addCase(searchMembers.pending, (state) => {
      state.isSearchLoading = true
    })
    .addCase(searchMembers.fulfilled, (state, action) => {
      state.isSearchError = false
      state.isSearchSuccess = true
      state.isSearchLoading = false

      state.searchUserResults = action.payload.searchResults
 
    })
    .addCase(searchMembers.rejected, (state, action) => {
      state.isSearchError = true
      state.isSearchSuccess = false
      state.isSearchLoading = false
      state.searchUsersErrorMessage = action.payload
    })
    // -----------------------------------------------------------------------
    .addCase(deleteConversation.pending, (state) => {
      state.deleteConversationLoading = true
    })
    .addCase(deleteConversation.fulfilled, (state, action) => {
      state.deleteConversationError = false
      state.deleteConversationSuccess = true
      state.deleteConversationLoading = false
      state.conversations = state.conversations.filter(conversation => conversation._id !== action.payload.conversationId)
    })
    .addCase(deleteConversation.rejected, (state, action) => {
      state.deleteConversationError = true
      state.deleteConversationSuccess = false
      state.deleteConversationLoading = false 
    }
    )
    .addCase(markConversationAsRead.pending, (state) => {
      state.markAsReadStatus = STATUS.LOADING
    })
    .addCase(markConversationAsRead.fulfilled, (state, action) => {
      
      const index = state.conversations.findIndex(conversation => conversation._id === action.payload.conversation._id)
    
      if (index !== -1) {
        state.conversations[index].newMessageForUserId = ""
      }
      state.markAsReadStatus = STATUS.SUCCESS
      

    })
    .addCase(markConversationAsRead.rejected, (state) => {
      state.markAsReadStatus = STATUS.ERROR
    })
    // -----------------------------------------------------------------------
      .addCase(sendPrayselyMessage.pending, (state) => {
        state.sendPrayselyMessageStatus = STATUS.LOADING
      })
      .addCase(sendPrayselyMessage.fulfilled, (state, action) => {
        state.sendPrayselyMessageStatus = STATUS.SUCCESS
      })
      .addCase(sendPrayselyMessage.rejected, (state) => {
        state.sendPrayselyMessageStatus = STATUS.ERROR  
      })
    // -----------------------------------------------------------------------
      .addCase(createConversation.pending, (state) => {
        state.createConversationStatus = STATUS.LOADING
      })
      .addCase(createConversation.fulfilled, (state, action) => {
        
        
        const newConversation = {
          ...action.payload.conversation,
          otherParticipant: action.payload.otherUser,
        }

        state.conversations.unshift(newConversation)

        state.createConversationStatus = STATUS.SUCCESS
        state.conversation = action.payload.conversation
        state.viewStartConversationModal = false
 

      })
      .addCase(createConversation.rejected, (state, action) => {
        state.createConversationStatus = STATUS.ERROR 
      })
      // &&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&
      .addCase(getMessages.pending, (state) => {
        state.getMessagesStatus = STATUS.LOADING
      })
      .addCase(getMessages.fulfilled, (state, action) => {

        state.getMessagesStatus = STATUS.SUCCESS 

        state.messages = action.payload.messages
        state.selectedConversation = action.payload.conversation 
        state.messagesNextCursor = action.payload.nextCursor || null

        // remove the unread status for the conversation in the conversations list since the user has now viewed the messages
        const conversationIndex = state.conversations.findIndex(conv => conv._id === action.payload.conversation._id)
        if (conversationIndex !== -1) {
            state.conversations[conversationIndex].isUnread = false
        }



      })
      .addCase(getMessages.rejected, (state, action) => {
        state.getMessagesStatus = STATUS.ERROR
      })
      // ============================================================
     
      .addCase(sendMessageWithConvId.pending, (state) => {
        state.sendMessageStatus = STATUS.LOADING
      })
      .addCase(sendMessageWithConvId.fulfilled, (state, action) => {
        state.sendMessageStatus = STATUS.SUCCESS 
        state.messages = [
            ...state.messages, 
            action.payload.message
        ]; 
        const conversationIndex = state.conversations.findIndex(conv => conv._id === action.payload.message.conversationId)
        if (conversationIndex !== -1) {
            state.conversations[conversationIndex].lastMessageText = action.payload.message.text
        }
      })
      .addCase(sendMessageWithConvId.rejected, (state, action) => {
        state.sendMessageStatus = STATUS.ERROR
      })
      // -----------------------------------------------------------------------
      .addCase(deleteMessage.pending, (state) => {
        state.isDeleteLoading = true
      })
      .addCase(deleteMessage.fulfilled, (state, action) => {
        state.isDeleteError = false
        state.isDeleteSuccess = true
        state.isCreateLoading = false
        state.messages = state.messages.filter(
          (ele) => ele._id !== action.payload.deletedMsgId
        )
      })
      .addCase(deleteMessage.rejected, (state, action) => {
        state.isDeleteError = true
        state.isDeleteSuccess = false
        state.isDeleteLoading = false
        state.deleteMessageError = action.payload
      })
  },
})

export const { 
  setPanelInfo, 
  conversationReset, 
  chooseConversation, 
  viewNoConversation, 
  setViewAllUsers, 
  resetAlreadyExists,
  resetCreateConversation,
  setViewStartConversationModal,
  resetDeleteData
} = conversationSlice.actions
export default conversationSlice.reducer